import ConfiguratorMenu from '../configurator-menu/configurator-menu';
import styles from './gift-card-layout.module.scss';
import {
  assert,
  assertSome,
  v2_selectFreefromConfigurationPropertyValue,
  v2_useInstantOrderCheckout,
  v2_useProduct,
} from '@crocodille/common';
import * as routing from '../../../routing';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GiftCardList from '@components/gift-cards-configurator/gift-card-list/gift-card-list';
import GiftCardDetail from '../gift-card-detail/gift-card-detail';
import GiftCard from '../gift-card/gift-card';
import { v2_useGiftCardProducts } from '@crocodille/common';
import ConfiguratorFilterMenu from '../configurator-filter-menu/configurator-filter-menu';
import { useTranslation } from 'next-i18next';
import ConfiguratorMenuItem from '../configurator-menu/configurator-menu-item';

export type ActiveStep = 'design' | 'personalize' | 'check';

interface GiftCardLayoutProps {
  readonly branch: string;
  readonly taxon: string;
  readonly product: string;
}

const GiftCardLayout: React.FC<React.PropsWithChildren<GiftCardLayoutProps>> = (
  props,
) => {
  const { t } = useTranslation();
  const { query, push } = useRouter();
  const [checkDataStep, setCheckDataStep] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const taxonSlug = query.taxon ?? props.taxon;
  assert(typeof taxonSlug === 'string');

  const slug = query.slug ?? props.product;
  assert(typeof slug === 'string');

  const {
    data: product,
    configurationValues,
    filteredConfiguration,
    setFreeformProperty,
    setSingleChoiceProperty,
    clearConfiguration,
  } = v2_useProduct(slug);

  const { data: configuratorProductsResponse } = v2_useGiftCardProducts();

  const { form, totalPrice, paymentMethods, error, isMutating } =
    v2_useInstantOrderCheckout(
      {
        product,
        filteredConfiguration,
        configurationValues,
      },
      (order) => {
        clearConfiguration();

        push(
          routing.checkoutPayment({
            tokenValue: assertSome(order?.tokenValue),
          }),
        );
      },
    );

  const configurationValuesRef = useRef(configurationValues);
  configurationValuesRef.current = configurationValues;

  const designProperty =
    product?.configuration?.find?.((property) => property.id === 'designId') ??
    null;

  useEffect(() => {
    if (designProperty == null) {
      return;
    }

    assert(designProperty != null);
  }, [designProperty]);

  let designIdValue: string | null = null;
  if (designProperty) {
    designIdValue = v2_selectFreefromConfigurationPropertyValue(
      configurationValues,
      designProperty.id,
    );
  }

  const selectedDesign = designProperty?.options.find(
    (option) => option.itemCode === designIdValue,
  );

  const fromWhoValue = v2_selectFreefromConfigurationPropertyValue(
    configurationValues,
    'fromWho',
  );

  const setFromWho = useCallback(
    (value: string) => {
      setFreeformProperty('fromWho', value.length === 0 ? null : value);
    },
    [setFreeformProperty],
  );

  const toWhoValue = v2_selectFreefromConfigurationPropertyValue(
    configurationValues,
    'toWho',
  );

  const setToWho = useCallback(
    (value: string) => {
      setFreeformProperty('toWho', value.length === 0 ? null : value);
    },
    [setFreeformProperty],
  );

  const isGiftValue =
    v2_selectFreefromConfigurationPropertyValue(
      configurationValues,
      'isGift',
    ) === 'true';

  const setIsGift = useCallback(
    (value: boolean) => {
      setFreeformProperty('isGift', value ? 'true' : 'false');
    },
    [setFreeformProperty],
  );

  const messageValue = v2_selectFreefromConfigurationPropertyValue(
    configurationValues,
    'message',
  );

  const setMessage = useCallback(
    (value: string) => {
      setFreeformProperty('message', value.length === 0 ? null : value);
    },
    [setFreeformProperty],
  );

  const emailValue = v2_selectFreefromConfigurationPropertyValue(
    configurationValues,
    'email',
  );

  const setEmail = useCallback(
    (value: string) => {
      setFreeformProperty('email', value.length === 0 ? null : value);
    },
    [setFreeformProperty],
  );

  const setVariant = useCallback(
    (value: string) => {
      setSingleChoiceProperty('variant', value.length === 0 ? null : value);
    },
    [setSingleChoiceProperty],
  );

  const designSelected = designIdValue != null;

  const activeStep = designSelected
    ? checkDataStep
      ? ('check' as const)
      : ('personalize' as const)
    : ('design' as const);

  const setActiveStep = useCallback(
    (step: ActiveStep) => {
      if (designProperty == null) {
        return;
      }
      if (step === 'check') {
        emailValue && setCheckDataStep(true);
      } else if (step === 'personalize') {
        setCheckDataStep(false);
      } else if (step === 'design') {
        setFreeformProperty(designProperty.id, null);
      }
    },
    [designProperty, setFreeformProperty, emailValue],
  );

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        block: 'end',
        behavior: 'smooth',
      });
    }
  }, [activeStep]);

  const productOptions =
    product?.configuration?.find?.((property) => property.id === 'variant')
      ?.options ?? null;

  const cardsDesigns = configuratorProductsResponse?.flatMap(
    (group) => group.digitalGiftCardDesigns,
  );

  const handleFilterChange = (groupId: number | null) => {
    setSelectedGroupId(groupId);
  };

  const filteredDesigns = useMemo(() => {
    if (selectedGroupId === null) return cardsDesigns;
    return cardsDesigns?.filter((design) =>
      configuratorProductsResponse?.some(
        (group) =>
          group.id === selectedGroupId &&
          group.digitalGiftCardDesigns.includes(design),
      ),
    );
  }, [selectedGroupId, cardsDesigns, configuratorProductsResponse]);

  if (!product) {
    return null;
  }

  return (
    <>
      <div className={styles.container}>
        <div ref={containerRef}>
          <ConfiguratorMenu isPlasticCardDisplayed={!designSelected}>
            <ConfiguratorMenuItem
              stepNumber="1"
              stepName="design"
              activeStep={activeStep}
              onClick={() => {
                setActiveStep;
                clearConfiguration();
              }}
            >
              {t('gift_card_configurator.design')}
            </ConfiguratorMenuItem>
            <ConfiguratorMenuItem
              stepNumber="2"
              stepName="personalize"
              activeStep={activeStep}
              onClick={setActiveStep}
              disabled={!designSelected}
            >
              {t('gift_card_configurator.personalize')}
            </ConfiguratorMenuItem>
            <ConfiguratorMenuItem
              stepNumber="3"
              stepName="check"
              activeStep={activeStep}
              onClick={setActiveStep}
              disabled={!emailValue}
            >
              {t('gift_card_configurator.check')}
            </ConfiguratorMenuItem>
          </ConfiguratorMenu>
        </div>
        <div className={styles.content}>
          {designIdValue == null ? (
            <>
              <ConfiguratorFilterMenu>
                <ConfiguratorFilterMenu.Item
                  label={t('gift_card_configurator.all')}
                  state={selectedGroupId === null ? 'active' : 'inactive'}
                  onClick={() => handleFilterChange(null)}
                />
                {configuratorProductsResponse?.map((group) => (
                  <ConfiguratorFilterMenu.Item
                    key={group.id}
                    label={group.label}
                    state={selectedGroupId === group.id ? 'active' : 'inactive'}
                    onClick={() => handleFilterChange(group.id)}
                  />
                ))}
              </ConfiguratorFilterMenu>
              <GiftCardList>
                {filteredDesigns?.map((o) => (
                  <GiftCard
                    key={o.id}
                    title={o.shortText ?? ''}
                    image={o.image}
                    onAdd={() => {
                      setFreeformProperty('designId', o.id.toString());
                      setActiveStep('personalize');
                    }}
                  />
                ))}
              </GiftCardList>
            </>
          ) : (
            <GiftCardDetail
              price={totalPrice ?? 0}
              toWho={toWhoValue ?? ''}
              onToWhoChange={setToWho}
              fromWho={fromWhoValue ?? ''}
              onFromWhoChange={setFromWho}
              isGift={isGiftValue}
              onIsGiftChange={setIsGift}
              message={messageValue ?? ''}
              onMessageChange={setMessage}
              email={emailValue ?? ''}
              onEmailChange={setEmail}
              activeStep={activeStep}
              nextStepName={activeStep === 'personalize' ? 'check' : 'design'}
              prevStepName={
                activeStep === 'personalize' ? 'design' : 'personalize'
              }
              setActiveStep={setActiveStep}
              form={form}
              paymentMethods={paymentMethods}
              error={error?.message}
              isBusy={isMutating}
              imgUrl={selectedDesign?.image}
              products={productOptions || []}
              clearConfiguration={clearConfiguration}
              onVariantChange={setVariant}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default GiftCardLayout;
